import { IconName } from 'components/elements';
import { filterListByKeys } from 'lib/filter';
import { isNotEmpty } from 'lib/misc';
import {
  BankTransaction,
  SystemTransaction,
  Transaction,
  TransactionDateType,
  TransactionState,
} from 'types/dto';

/**
 * Check whether both amounts are equal
 *
 * @param {number} left Left amount
 * @param {number} right Right amount
 */
const isAmountEqual = (left: number, right: number) => left.toFixed(2) === right.toFixed(2);

/**
 * Check if the specified list of transactions can be archived
 *
 * @param {Array<Transaction>} transactions Source transactions
 */
export const canArchiveTransactions = (transactions: Array<Transaction>) =>
  // Ensure there is at least one transaction present
  transactions.length > 0 &&
  // Ensure all transactions are unmatched
  transactions.every(isUnmatchedTransaction);

/**
 * Check if the specified transactions can be matched
 *
 * @param {Array<Transaction>} bankTransactions Source bank transactions
 * @param {Array<Transaction>} systemTransactions Source system transactions
 */
export const canMatchTransactions = (
  bankTransactions: Array<BankTransaction>,
  systemTransactions: Array<SystemTransaction>,
) =>
  // Ensure there is at least one bank transaction present
  bankTransactions.length > 0 &&
  // Ensure there is at least one system transaction present
  systemTransactions.length > 0 &&
  // Ensure all bank transactions are unmatched
  bankTransactions.every(isUnmatchedTransaction) &&
  // Ensure all system transactions are unmatched
  systemTransactions.every(isUnmatchedTransaction) &&
  // Ensure both transaction batch totals are within the match threshold
  isAmountEqual(getTransactionTotal(bankTransactions), getTransactionTotal(systemTransactions));

export const canAutoMatchTransactions = (
  bankTransactions: Array<BankTransaction>,
  systemTransactions: Array<SystemTransaction>,
) =>
  // Ensure there is at least one bank transaction present
  bankTransactions.length > 0 &&
  // Ensure there is at least one system transaction present
  systemTransactions.length > 0 &&
  // Ensure all bank transactions are unmatched
  bankTransactions.every(isUnmatchedTransaction) &&
  // Ensure all system transactions are unmatched
  systemTransactions.every(isUnmatchedTransaction) &&
  // Ensure both transaction batch totals are within the match threshold
  isAmountEqual(getTransactionTotal(bankTransactions), getTransactionTotal(systemTransactions));

/**
 * Check if the specified list of transactions can be restored
 *
 * @param {Array<Transaction>} transactions Source transactions
 */
export const canRestoreTransactions = (transactions: Array<Transaction>) =>
  // Ensure there is at least one transaction present
  transactions.length > 0 &&
  // Ensure all transactions are archived
  transactions.every(isArchivedTransaction);

/**
 * Check if the specified list of transactions can be unmatched
 *
 * @param {Array<Transaction>} transactions Source transactions
 */
export const canUnmatchTransactions = (transactions: Array<Transaction>) =>
  // Ensure there is at least one transaction present
  transactions.length > 0 &&
  // Ensure all transactions are unmatched
  transactions.every(isMatchedTransaction);

/**
 * Check if the specified transaction is archived
 *
 * @param {Transaction} transaction Source transaction
 */
export const isArchivedTransaction = (transaction: Transaction) =>
  transaction.mappedState === 'IGNORED';

/**
 * Check if the specified transaction is matched
 *
 * @param {Transaction} transaction Source transaction
 */
export const isMatchedTransaction = (transaction: Transaction) =>
  transaction.mappedState === 'MATCHED';

/**
 * Check if the specified transaction is unmatched
 *
 * @param {Transaction} transaction Source transaction
 */
export const isUnmatchedTransaction = (transaction: Transaction) =>
  transaction.mappedState === 'UNMATCHED';

/**
 * Check if any of the specified transactions are archived
 *
 * @param {Array<Transaction>} transactions Source transactions
 */
export const hasArchivedTransactions = (transactions: Array<Transaction>) =>
  transactions.some(isArchivedTransaction);

/**
 * Check if any of the specified transactions are matched
 *
 * @param {Array<Transaction>} transactions Source transactions
 */
export const hasMatchedTransactions = (transactions: Array<Transaction>) =>
  transactions.some(isMatchedTransaction);

/**
 * Extract transaction identifier
 *
 * @param {Transaction} transaction Source transaction
 */
export const getTransactionId = <TTransaction extends Transaction>(transaction: TTransaction) =>
  transaction.id;

/**
 * Extract transaction identifiers
 *
 * @param {Array<Transaction>} transactions Source transactions
 */
export const getTransactionIds = (transactions: Array<Transaction>) =>
  transactions.map(getTransactionId);

/**
 * Extract transaction mapping identifier
 *
 * @param {Array<Transaction>} transactions Source transactions
 */
export const getTransactionMappingIds = (transactions: Array<Transaction>) =>
  transactions.map(transaction => transaction.transactionMappingId).filter(isNotEmpty);

/**
 * Get human readable transaction date type description
 *
 * @param {TransactionDateType} transactionDateType Source transaction date type
 */
export const getTransactionDateTypeName = (transactionDateType?: TransactionDateType) => {
  switch (transactionDateType) {
    case 'PAYMENT':
      return 'Payment';
    case 'POSTING':
      return 'Posting';
    default:
      return;
  }
};

/**
 * Get human readable transaction state description
 *
 * @param {TransactionState} transactionState Source transaction state
 */
export const getTransactionStateLabel = (transactionState?: TransactionState) => {
  switch (transactionState) {
    case 'IGNORED':
      return 'Archived';
    case 'MATCHED':
      return 'Matched';
    case 'UNMATCHED':
      return 'Unmatched';
    default:
      return 'All';
  }
};

/**
 * Get total amount for all specified transactions
 *
 * @param {Array<Transaction>} transactions Source transactions
 */
export const getTransactionTotal = (transactions: Array<Transaction>) =>
  transactions.reduce<number>((acc, transaction) => acc + transaction.amount, 0);

/**
 * Get name of the icon associated with the specified transaction
 *
 * @param {Transaction} transaction Source transaction
 */
export const getTransactionIcon = (transaction: Transaction): IconName => {
  switch (transaction.mappedState) {
    case 'IGNORED':
      return 'transaction archived';
    case 'MATCHED':
      return 'transaction matched';
    case 'UNMATCHED':
    default:
      return 'transaction unmatched';
  }
};

/**
 * Clear transaction mapping status for transactions with the specified mapping identifier
 *
 * @param {Record<string, Transaction>} sourceMap Source transaction map
 * @param {Array<string>} mappingIds List of mapping identifiers to clear
 */
const clearTransactionMapStatus = <TTransaction extends Transaction>(
  sourceMap: Record<string, TTransaction>,
  mappingIds: Array<string>,
) =>
  Object.values(sourceMap)
    .filter(
      entity => entity.transactionMappingId && mappingIds.includes(entity.transactionMappingId),
    )
    .map<TTransaction>(transaction => ({
      ...transaction,
      mappedState: 'UNMATCHED',
      transactionMappingId: null,
    }))
    .reduce(
      (acc, transaction) => ({ ...acc, [getTransactionId(transaction)]: transaction }),
      sourceMap,
    );

/**
 * Clear bank transaction mapping status for transactions with the specified mapping identifier
 *
 * @param {Record<string, Transaction>} sourceMap Source transaction map
 * @param {Array<string>} mappingIds List of mapping identifiers to clear
 */
export const clearBankTransactionMapStatus = (
  sourceMap: Record<string, BankTransaction>,
  mappingIds: Array<string>,
) => clearTransactionMapStatus(sourceMap, mappingIds);

/**
 * Clear system transaction mapping status for transactions with the specified mapping identifier
 *
 * @param {Record<string, Transaction>} sourceMap Source transaction map
 * @param {Array<string>} mappingIds List of mapping identifiers to clear
 */
export const clearSystemTransactionMapStatus = (
  sourceMap: Record<string, SystemTransaction>,
  mappingIds: Array<string>,
) => clearTransactionMapStatus(sourceMap, mappingIds);

/**
 * Set transaction property values for transactions with the specified identifiers
 *
 * @param {Record<string, Transaction>} sourceMap Source transaction map
 * @param {Array<string>} subjectIds List of transaction identifiers to update
 * @param {Partial<Transaction>>} patch List of changes to apply to each transaction
 */
const setTransactionProps = <TTransaction extends Transaction>(
  sourceMap: Record<string, TTransaction>,
  subjectIds: Array<string>,
  patch: Partial<TTransaction>,
) =>
  subjectIds
    .map(id => sourceMap[id])
    .filter(isNotEmpty)
    .map<TTransaction>(transaction => ({ ...transaction, ...patch }))
    .reduce(
      (acc, transaction) => ({ ...acc, [getTransactionId(transaction)]: transaction }),
      sourceMap,
    );

/**
 * Set transaction mapping status for transactions with the specified identifiers
 *
 * @param {Record<string, Transaction>} sourceMap Source transaction map
 * @param {Array<string>} subjectIds List of transaction identifiers to update
 * @param {string} mappingId Target mapping identifier
 * @param {TransactionState} transactionState Target transaction state
 */
export const setTransactionMappingStatus = <TTransaction extends Transaction>(
  sourceMap: Record<string, TTransaction>,
  subjectIds: Array<string>,
  mappingId: string,
  transactionState: TransactionState,
) =>
  setTransactionProps(sourceMap, subjectIds, {
    mappedState: transactionState,
    transactionMappingId: mappingId,
  } as Partial<TTransaction>);

/**
 * Set transaction note status for transactions with the specified identifiers
 *
 * @param {Record<string, Transaction>} sourceMap Source transaction map
 * @param {Array<string>} subjectIds List of transaction identifiers to update
 * @param {boolean} hasNotes TRUE if transactions have notes, FALSE otherwise
 */
export const setTransactionNoteStatus = <TTransaction extends Transaction>(
  sourceMap: Record<string, TTransaction>,
  subjectIds: Array<string>,
  hasNotes: boolean,
) =>
  setTransactionProps(sourceMap, subjectIds, {
    hasNotes,
  } as Partial<TTransaction>);

/**
 * Find transactions in the pool of available ones with matching mapping ids
 *
 * @param {Array} source Source list of transactions to use as parents
 * @param {Array} pool Target transaction pool
 */
export const findRelatedTransactions = <
  TSourceTransaction extends Transaction,
  TPoolTransaction extends Transaction = TSourceTransaction,
>(
  source: Array<TSourceTransaction>,
  pool: Array<TPoolTransaction>,
) => {
  // Extract source transaction ids
  const sourceIds = new Set(getTransactionIds(source));

  // Extract non-empty mapping identifiers from the source transactions
  const sourceMappingIds = new Set(
    source.map(transaction => transaction.transactionMappingId).filter(isNotEmpty),
  );

  // Remove source transactions from the pool and
  // find other transactions that have matching mapping ids
  return (
    pool
      .filter(transaction => !sourceIds.has(transaction.id))
      .filter(transaction => !!transaction.transactionMappingId)
      // @ts-ignore
      .filter(transaction => sourceMappingIds.has(transaction.transactionMappingId))
  );
};

/**
 * Find transactions with matching identifiers
 *
 * @param {Array} transactions Source list of transactions to filter
 * @param {Array} ids Target identifiers
 */
export const filterTransactionsByIds = <TTransaction extends Transaction>(
  transactions: Array<TTransaction>,
  ids: Array<string>,
) => filterListByKeys(transactions, ids, getTransactionId);
